import React, { useEffect } from "react";

import { Player } from "./game/Player";
import { Me } from "./game/Me";
import { Table } from "./game/Table";
import { GameStage } from "../types/types";
import {
  selectCurrentGameId,
  selectCurrentMatchId,
  selectGameIndex,
  selectGameStage,
} from "../store/selectors";
import { useSelector } from "react-redux";
import { usePositionedGamePlayerIds } from "../hooks/usePositionedGamePlayerIds";
import { useConnectGame } from "../hooks/useConnectGame";
import { GameDetails } from "./game/GameDetails";
import { useCurrentUser } from "../hooks/useCurrentUser";

const ConnectedGame: React.FC = () => {
  const stage = useSelector(selectGameStage);
  const positionedPlayerIds = usePositionedGamePlayerIds();
  const gameIndex = useSelector(selectGameIndex);
  const { setViewingScores } = useCurrentUser();

  // When game finishes, automatically show the scoreboard
  useEffect(() => {
    if (stage === GameStage.Finished) {
      setViewingScores(true);
    }
  }, [stage, setViewingScores]);

  if (
    stage === undefined ||
    positionedPlayerIds === undefined ||
    gameIndex === undefined
  ) {
    return <div>loading stage and positionedPlayerIds</div>;
  }

  // Game finished state is now handled by showing scoreboard via Match.tsx
  if (stage === GameStage.Finished) {
    return <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 flex items-center justify-center">
      <div className="text-emerald-200 animate-pulse">Loading scores...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950">
      <div className="grid h-screen w-full p-4 grid-rows-[1fr_auto_auto] grid-cols-4 gap-4">
        <div className="row-start-1 col-start-1 col-span-3 flex">
          <Table />
        </div>
        <div className="row-start-2 col-start-1 col-span-3">
          <Me />
        </div>
        <div className="row-start-3 col-start-1">
          <Player playerId={positionedPlayerIds.firstFromMe} />
        </div>
        <div className="row-start-3 col-start-2">
          <Player playerId={positionedPlayerIds.secondFromMe} />
        </div>
        <div className="row-start-3 col-start-3">
          <Player playerId={positionedPlayerIds.thirdFromMe} />
        </div>
        <div className="row-start-1 col-start-4 row-span-full flex">
          <GameDetails />
        </div>
      </div>
    </div>
  );
};

const ConnectGame: React.FC<{ matchId: string; gameId: string }> = ({
  matchId,
  gameId,
  children,
}) => {
  const gameConnectionStatus = useConnectGame({ matchId, gameId });
  switch (gameConnectionStatus) {
    case "error":
      return <div>'Failed to connect to Firestore'</div>;
    case "success":
      return <>{children}</>;
  }
};

export const Game: React.FC = () => {
  const matchId = useSelector(selectCurrentMatchId);
  const gameId = useSelector(selectCurrentGameId);

  if (matchId === undefined || gameId === undefined) {
    return <div>loading matchId and gameId</div>;
  }

  return (
    <ConnectGame matchId={matchId} gameId={gameId}>
      <ConnectedGame />
    </ConnectGame>
  );
};
